import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useLayoutEffect, useEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { getPlacement } from '../utils/index.js';

const BasePositioner = ({ reference, content, offset = [0], onShow, onHide, isOpen, onClose, placement = 'bottom-left', zIndex, onPositionChange, portalContainer = document.body, }) => {
    const baseRef = useRef(null);
    const contentRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [ready, setReady] = useState(false);
    const updatePosition = useCallback(() => {
        if (!baseRef.current || !contentRef.current) {
            onClose();
            return;
        }
        const refRect = baseRef.current.getBoundingClientRect();
        if (refRect.top + refRect.bottom + refRect.left + refRect.right === 0
            || refRect.width === 0
            || refRect.height === 0) {
            onClose();
            return;
        }
        const contentRect = contentRef.current.getBoundingClientRect();
        let finalPlacement = placement;
        const adjustPlacement = (basePlacement) => {
            const availableSpaceRight = window.innerWidth - refRect.left;
            const availableSpaceLeft = refRect.right;
            return `${basePlacement}-${availableSpaceRight >= availableSpaceLeft ? 'left' : 'right'}`;
        };
        const calculated = getPlacement(refRect, contentRect, finalPlacement);
        const fitsBelow = window.innerHeight - refRect.bottom >= contentRect.height + 4;
        const fitsAbove = refRect.top >= contentRect.height + 4;
        if (placement.startsWith('bottom') && !fitsBelow && fitsAbove) {
            finalPlacement = placement.replace('bottom', 'top');
        }
        else if (placement.startsWith('top') && !fitsAbove && fitsBelow) {
            finalPlacement = placement.replace('top', 'bottom');
        }
        if (calculated) {
            const rightEdge = calculated.left + contentRect.width;
            const leftEdge = calculated.left;
            const needsPlacementAdjustment = finalPlacement.startsWith('auto');
            if (rightEdge > window.innerWidth && needsPlacementAdjustment) {
                finalPlacement = adjustPlacement(finalPlacement.split('-')[0]);
            }
            if (leftEdge < 0 && needsPlacementAdjustment) {
                finalPlacement = adjustPlacement(finalPlacement.split('-')[0]);
            }
        }
        setPosition(calculated);
        if (onPositionChange && calculated) {
            onPositionChange({
                top: calculated.top,
                left: calculated.left,
                placement: finalPlacement,
                referenceWidth: refRect.width,
            });
        }
    }, [placement, onClose, onPositionChange]);
    useLayoutEffect(() => {
        if (ready)
            requestAnimationFrame(updatePosition);
    }, [ready, updatePosition]);
    useEffect(() => {
        if (!isOpen)
            return;
        const onScroll = () => requestAnimationFrame(updatePosition);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [isOpen, updatePosition]);
    useEffect(() => {
        if (!isOpen || !baseRef.current || !contentRef.current)
            return;
        const observer = new ResizeObserver(() => requestAnimationFrame(updatePosition));
        observer.observe(baseRef.current);
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, [isOpen, updatePosition]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape')
            onClose();
    }, [onClose]);
    const handleClickOutside = useCallback((e) => {
        if (contentRef.current
            && !contentRef.current.contains(e.target)
            && baseRef.current
            && !baseRef.current.contains(e.target)) {
            onClose();
        }
    }, [onClose]);
    useEffect(() => {
        if (isOpen) {
            onShow?.();
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('click', handleClickOutside);
            setReady(true);
        }
        else {
            onHide?.();
            setReady(false);
            setPosition(null);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen, onShow, onHide, handleKeyDown, handleClickOutside]);
    return (jsxs(Fragment, { children: [cloneElement(reference, {
                ref: (node) => {
                    if (node)
                        baseRef.current = node;
                },
            }), isOpen
                && createPortal(jsx("div", { ref: contentRef, style: {
                        position: 'absolute',
                        top: position?.top ?? 0,
                        left: position?.left ?? 0,
                        visibility: position ? 'visible' : 'hidden',
                        zIndex: zIndex || 9999,
                        padding: `${offset[0]}px ${offset[1]}px`,
                    }, children: content }), portalContainer || document.body)] }));
};
BasePositioner.displayName = 'BasePositioner';

export { BasePositioner };
